"use client";

import { useSearchParams } from "next/navigation";
import {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { LuGlobe, LuUsers as LuUsers2 } from "react-icons/lu";

import { EmptyState } from "@/components/shared/empty-state";
import { StickyStack } from "@/components/shared/sticky-stack";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { FeedProviderInternal, useFeedLogic } from "@/contexts/feed-context";
import { UserContext } from "@/contexts/user-context";
import { PostsOrderBy } from "@/graphql/codegen/graphql";
import { useIsClient } from "@/hooks/use-is-client";
import type { GetPostsDTO } from "@/hooks-api/use-get-posts";
import { useGetPosts } from "@/hooks-api/use-get-posts";
import { isAbortError } from "@/utils/helpers";

import { Feed } from "../../feed";
import { FeedPostSkeletonList } from "../feed-post-skeleton-list";
import { useFeedWrapperContext } from "../feed-wrapper-context";
import { FeedWrapperTabs, FeedWrapperTabsSkeleton } from "../feed-wrapper-tabs";
import { FeedSearchHeader } from "./feed-search-header";
import { SearchUsersContent } from "./search-users-content";

export enum FEED_SEARCH_TAB_ITEMS {
  POSTS = "posts",
  USERS = "users",
}

const FeedSearch = () => {
  const searchParams = useSearchParams();
  const searchTerm = searchParams.get("search") ?? "";

  const [loading, setLoading] = useState(true);

  const { toggleBlockUser } = useContext(UserContext);
  const { activeTab, setActiveTab } = useFeedWrapperContext();
  const feedLogic = useFeedLogic({
    enableAdminPinnedPostFilters: false,
  });
  const { setPosts, initializePosts } = feedLogic;
  const isClient = useIsClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  const { getBasePostsRequest } = useGetPosts();

  const tabItems = [
    {
      value: FEED_SEARCH_TAB_ITEMS.POSTS,
      icon: <LuGlobe className="text-lg" />,
      label: "Posts",
    },
    {
      value: FEED_SEARCH_TAB_ITEMS.USERS,
      icon: <LuUsers2 className="text-lg" />,
      label: "Users",
    },
  ];

  const showEmptyState =
    !feedLogic.posts.length &&
    !loading &&
    activeTab === FEED_SEARCH_TAB_ITEMS.POSTS;
  const scrollRestorationKey = `search-feed:${searchTerm}:${activeTab}`;

  const getPostsBySearchTerm = useCallback(
    async ({
      filters,
      offset = 0,
      orderBy = PostsOrderBy.TimestampDesc,
    }: GetPostsDTO) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      return getBasePostsRequest({
        filters: {
          ...filters,
          body: {
            includesInsensitive: searchTerm,
          },
        },
        orderBy,
        offset,
        context: {
          fetchOptions: {
            signal: abortController.signal,
          },
        },
      });
    },
    [getBasePostsRequest, searchTerm],
  );

  const initialSearchRequest = useCallback(async () => {
    setLoading(true);

    try {
      const { posts } = await getPostsBySearchTerm({ offset: 0 });
      initializePosts(posts);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  }, [getPostsBySearchTerm, initializePosts]);

  useEffect(() => {
    if (activeTab === FEED_SEARCH_TAB_ITEMS.POSTS) {
      void initialSearchRequest();
    } else {
      setLoading(false);
    }
  }, [activeTab, searchTerm]);

  return (
    <FeedProviderInternal {...feedLogic} {...{ onUserBlock: toggleBlockUser }}>
      <div className="relative min-h-safe-screen w-full">
        <FeedSearchHeader {...{ activeTab, searchParams, searchTerm }} />

        <Tabs
          defaultValue={activeTab}
          className="w-full border-b border-border"
          onValueChange={(val) => {
            if (val === activeTab) return;
            setActiveTab(val as FEED_SEARCH_TAB_ITEMS);
            setPosts([]);
          }}
        >
          <StickyStack
            className="
            z-20 flex w-full bg-background/80 p-0 backdrop-blur-md
          "
          >
            {loading || !isClient ? (
              <FeedWrapperTabsSkeleton {...{ tabItems }} />
            ) : (
              <FeedWrapperTabs {...{ tabItems }} />
            )}
          </StickyStack>

          {isClient &&
            Object.values(FEED_SEARCH_TAB_ITEMS).map((tab) => {
              return (
                <TabsContent key={tab} value={tab}>
                  {loading ? (
                    <FeedPostSkeletonList count={10} withLeadingBorder />
                  ) : (
                    <>
                      {showEmptyState && (
                        <div
                          className="
                            relative top-10 flex h-safe-screen items-start
                            justify-center px-12 text-center
                            md:px-4
                          "
                        >
                          <EmptyState
                            image="/assets/empty-30.png"
                            title="No posts found."
                            description="Try searching for something else!"
                          />
                        </div>
                      )}

                      {!showEmptyState &&
                        tab === FEED_SEARCH_TAB_ITEMS.POSTS && (
                          <Feed
                            getPosts={getPostsBySearchTerm}
                            scrollRestorationKey={scrollRestorationKey}
                          />
                        )}
                      {!showEmptyState &&
                        tab === FEED_SEARCH_TAB_ITEMS.USERS && (
                          <SearchUsersContent {...{ searchTerm }} />
                        )}
                    </>
                  )}
                </TabsContent>
              );
            })}
        </Tabs>
      </div>
    </FeedProviderInternal>
  );
};

const FeedSearchWrapper = () => (
  <Suspense>
    <FeedSearch />
  </Suspense>
);

export default FeedSearchWrapper;
